import React, { useMemo } from 'react';
import type { Table } from '@tanstack/react-table';

import IconButton from 'components/partials/button/icon-button';

import Icons from 'assets/icons';

interface PaginationProps {
  table: Table<unknown>;
  pagesAmount: number;
  pageIndex: number;
  pageSize: number;
  totalRecords: number;
  showRecordsText?: boolean;
}

export const Pagination = ({
  table,
  pagesAmount,
  pageIndex,
  pageSize,
  totalRecords,
  showRecordsText = true,
}: PaginationProps): React.JSX.Element => {
  const showTableFeature = pagesAmount > 1;

  const rows = table.getRowModel().rows;

  const { showingFrom, showingTo } = useMemo(() => {
    const parentRowCount = rows.filter((row) => row.depth === 0).length;
    const from = pageIndex * pageSize + 1;
    const to = pageIndex * pageSize + parentRowCount;

    return { showingFrom: from, showingTo: to };
  }, [pageIndex, pageSize, rows]);

  return (
    <div className="flex gap-2 items-center" data-testid="pagination">
      {showRecordsText && (
        <span
          className="text-base text-gray-500 whitespace-nowrap"
          data-testid="showing-records-amount"
        >
          {`${showingFrom}-${showingTo} of ${totalRecords}`}
        </span>
      )}

      <div className="flex items-start gap-1">
        <IconButton
          variant="reserve-text"
          size="sm"
          icon={<Icons.DoubleArrow customClass="rotate-180" />}
          disabled={!showTableFeature || pageIndex === 0}
          onClick={() => table.setPageIndex(0)}
          aria-label="First page"
          data-testid="pagination-button-start"
        />

        <IconButton
          variant="reserve-text"
          size="sm"
          icon={<Icons.ChevronLeft />}
          disabled={!showTableFeature || pageIndex === 0}
          onClick={() => table.previousPage()}
          aria-label="Previous page"
          data-testid="pagination-button-previous"
        />

        <IconButton
          variant="reserve-text"
          size="sm"
          icon={<Icons.ChevronRight />}
          disabled={!showTableFeature || pageIndex + 1 === table.getPageCount()}
          onClick={() => table.nextPage()}
          aria-label="Next page"
          data-testid="pagination-button-next"
        />

        <IconButton
          variant="reserve-text"
          size="sm"
          icon={<Icons.DoubleArrow />}
          disabled={!showTableFeature || pageIndex + 1 === table.getPageCount()}
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          aria-label="Last page"
          data-testid="pagination-button-end"
        />
      </div>
    </div>
  );
};
